import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 30,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      ease: "easeOut",
      duration: 0.5,
    },
  },
  exit: {
    opacity: 0,
    y: -30,
    transition: {
      duration: 0.3,
    },
  },
};

// 인앱웹뷰 감지 함수
const isInAppBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /wv|KAKAOTALK|Line|NAVER|FBAN|Instagram/.test(userAgent);
};

function KakaoAuthPage() {
  const navigate = useNavigate();
  const [isInApp, setIsInApp] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    // 인앱 브라우저 감지
    setIsInApp(isInAppBrowser());
  }, []);

  const handleKakaoAuth = (e) => {
    // 버튼 클릭 시 기본 동작 방지
    e.preventDefault();
    e.stopPropagation();

    // 네비게이션 상태 설정
    setIsNavigating(true);

    // 즉시 페이지 이동 (애니메이션 없이)
    navigate("/verification");
  };

  return (
    <div
      className="page-container"
      style={{
        overflow: "hidden",
        position: "relative",
        height: "100vh",
        width: "100%",
      }}
    >
      <motion.div
        className="page-header"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <h1>공동소유 위임 동의</h1>
      </motion.div>

      <div className="kakao-auth-content">
        {isInApp && (
          <div
            className="inapp-browser-notice"
            style={{
              margin: "0 20px 15px",
              padding: "15px",
              backgroundColor: "#f8f9fa",
              borderRadius: "12px",
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              border: "1px solid #e9ecef",
            }}
          >
            <div className="notice-icon" style={{ marginTop: "2px" }}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                  stroke="#007AFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 16V12"
                  stroke="#007AFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 8H12.01"
                  stroke="#007AFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#495057",
                lineHeight: "1.5",
              }}
            >
              카카오톡 앱 내에서는 일부 기능이 제한될 수 있습니다. 외부
              브라우저(Safari, Chrome)로 접속하시면 더 원활하게 이용하실 수
              있습니다.
            </p>
          </div>
        )}
        {!isInApp && (
          <motion.div
            className="logo-container"
            style={{ marginBottom: "-10px" }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.img
              src="/svg.png"
              alt="확인 아이콘"
              style={{ width: "160px", height: "160px", marginBottom: "10px" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            />
          </motion.div>
        )}

        <motion.div
          className="auth-message"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="delegation-info" style={{ textAlign: "center" }}>
            <motion.h2
              style={{
                marginBottom: 20,
                textAlign: "center",
              }}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              공동소유 위임 안내
            </motion.h2>
            <motion.p
              style={{ textAlign: "center" }}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              본 절차를 통해 공동소유 부동산에 대한 <br /> 의결권을 대표자
              회의에 위임합니다.
            </motion.p>
          </div>
        </motion.div>

        <div style={{ height: "100px" }}></div>
      </div>

      {/* 하단 버튼 영역 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: 0.5,
          duration: 0.5,
        }}
        style={{
          position: "fixed",
          bottom: "40px",
          left: "0",
          right: "0",
          padding: "20px 24px 40px",
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          paddingBottom: "env(safe-area-inset-bottom, 40px)",
        }}
        onAnimationComplete={() => {
          // 애니메이션 완료 후 motion.div를 일반 div로 변환
          const buttonContainer = document.querySelector(".button-container");
          if (buttonContainer) {
            buttonContainer.style.transform = "none";
            buttonContainer.style.transition = "none";
          }
        }}
        className="button-container"
      >
        {/* 위임 동의 진행하기 버튼 */}
        <button
          onClick={handleKakaoAuth}
          style={{
            width: "100%",
            height: "56px",
            backgroundColor: "#7B42F6",
            color: "#FFFFFF",
            border: "none",
            borderRadius: "12px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "background-color 0.2s ease",
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTapHighlightColor: "transparent",
            outline: "none",
            pointerEvents: "auto",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#6A35E5";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#7B42F6";
          }}
          onTouchStart={(e) => {
            e.target.style.backgroundColor = "#6A35E5";
          }}
          onTouchEnd={(e) => {
            e.target.style.backgroundColor = "#7B42F6";
          }}
        >
          위임 동의 진행하기
        </button>
      </motion.div>
    </div>
  );
}

export default KakaoAuthPage;
